import React__default, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './styles.module.scss.js';
import propsToClass from '../../../helpers/propsToClass.js';
import uniqueId from '../../../helpers/uniqueId.js';
import { COLORS } from '../../../helpers/constants.js';

/**
 * @description Nimvelo Input component
 * @props autoFocus, checkValidity, className, color, disabled, fullWidth, inputProps, labelAlign, labelPosition, labelText, onBlur,
 * onChange, placeholder, reserveErrorSpace, required, size, type, useBrowserValidation, useBrowserValidationMessage
 * validateTrigger, validationError, validtorFunction, value, width
 */
const NvInput = ({ autoFocus = false, checkValidity = () => { }, className = '', color, noColorOnHover = false, disabled = false, hideUnderline = false, inputProps, labelAlign = 'left', labelPosition = 'left', labelText, onBlur = () => { }, onChange: userOnChange = () => { }, placeholder, reserveErrorSpace, required = false, size = 'standard', type = 'text', useBrowserValidation = false, useBrowserValidationMessage = false, validateTrigger = 'blurOnce', validationError, validatorFunction, value: userValue, }) => {
    const [validatorValue, setValidatorValue] = useState('');
    const [hasBlurred, setHasBlurred] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [id] = useState(uniqueId('input'));
    /**
     * Allow implementor to control value even with validator function
     */
    useEffect(() => {
        const hasValue = userValue !== undefined && userValue !== null;
        if (validatorFunction && hasValue) {
            setValidatorValue(userValue || '');
        }
    }, [userValue, validatorFunction]);
    /**
     * Internally control input value and error message when using validatorFunction
     * @param e
     */
    const validatorOnChange = (e) => {
        setValidatorValue(e.target.value);
        if ((hasBlurred && validateTrigger === 'blurOnce') ||
            validateTrigger === 'onChange') {
            setErrorMessage(validatorFunction?.(e.target.value) || '');
        }
    };
    /**
     * calls the validator function on blur events
     */
    const triggerValidatorFunction = () => {
        if (!hasBlurred && validateTrigger === 'blurOnce') {
            setHasBlurred(true);
        }
        setErrorMessage(validatorFunction ? validatorFunction(validatorValue) : '');
    };
    // set browser validation message on mount and when value changes
    const inputRef = useRef(null);
    useEffect(() => {
        if (useBrowserValidation &&
            useBrowserValidationMessage &&
            inputRef.current) {
            setErrorMessage(inputRef.current.validationMessage);
        }
    }, [useBrowserValidation, useBrowserValidationMessage, userValue]);
    /**
     * Call checkvalidity on mount and when value changes
     */
    const memoisedCheckValidity = useCallback(checkValidity, []);
    useEffect(() => {
        if (inputRef?.current?.validity && useBrowserValidation) {
            const i = inputRef.current;
            memoisedCheckValidity(i.validity.valid);
        }
    }, [useBrowserValidation, memoisedCheckValidity, userValue]);
    const label = (React__default.createElement("label", { className: `${styles.labelStyle} ${styles[labelAlign]}`, htmlFor: id }, labelText));
    const wrapperClasses = `${styles.wrapper} ${styles[labelPosition] ?? styles.left} ${styles[size] ?? styles.standard} ${className}`;
    const validationContents = (validatorFunction || (useBrowserValidation && useBrowserValidationMessage)
        ? errorMessage
        : validationError) ||
        (reserveErrorSpace && '\u00A0');
    return (React__default.createElement("div", { className: propsToClass(wrapperClasses, [[styles.disabled, disabled]]) },
        labelText && label,
        React__default.createElement("div", { className: styles.input_wrapper },
            React__default.createElement("input", { ref: inputRef, className: propsToClass(styles.inputArea, [
                    [styles.browser_validation, useBrowserValidation],
                    [styles.underline, !hideUnderline],
                    [styles.coloredHover, !noColorOnHover],
                ]), type: type, id: id, disabled: disabled, value: validatorFunction ? validatorValue : userValue, onChange: validatorFunction ? validatorOnChange : userOnChange, onBlur: (e) => {
                    if (color?.borderBlur) {
                        e.target.style.borderBottomColor = color.borderBlur;
                    }
                    else {
                        e.target.style.removeProperty('border-bottom-color');
                    }
                    if (color?.text) {
                        e.target.style.color = color.text;
                    }
                    else {
                        e.target.style.removeProperty('color');
                    }
                    if (validatorFunction) {
                        triggerValidatorFunction();
                    }
                    else {
                        onBlur();
                    }
                }, onFocus: (e) => {
                    e.target.style.borderBottomColor =
                        color?.borderFocus || COLORS.PRIMARY;
                    e.target.style.color = color?.textFocus || COLORS.PRIMARY;
                }, style: {
                    backgroundColor: color?.background,
                    color: color?.text,
                    borderBottomColor: color?.borderBlur,
                }, 
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus: autoFocus, required: required, placeholder: placeholder, ...inputProps }),
            required && useBrowserValidation && (React__default.createElement("span", { className: styles.required }, "Required *")),
            !!validationContents && (React__default.createElement("span", { className: styles.error }, validationContents)))));
};

export { NvInput, NvInput as default };
//# sourceMappingURL=component.js.map
